import { useState, type SVGProps, type ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  AlertBanner,
  Badge,
  Button,
  DataTable,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  EmptyState,
  Heading,
  Popover,
  PopoverContent,
  PopoverTrigger,
  SearchInput,
  SelectField,
  Text,
  Textarea,
  tabularNumsClass,
  type DataTableColumn,
} from "@lendcord/concord";
import { useListRoleUpgradeRequestsQuery, useUpgradeUserRoleMutation, useGetAdminUserProfileQuery } from "../api/members.api";
import { isQueryApiError } from "@/app/store/baseApi";
import { formatTimestampDate, formatWireDate } from "@/app/dateFormat";
import type {
  RoleUpgradeRequestListItem,
  RoleUpgradeStatusFilter,
} from "../members.types";
import { ROLE_UPGRADE_STATUS_FILTERS } from "../members.types";

const PAGE_SIZE = 10;

const STATUS_BADGE: Record<string, "success" | "warning" | "info" | "error"> = {
  IN_PROGRESS: "info",
  APPROVED: "success",
  REJECTED: "error",
};

type SortKey = "readableId" | "firstName" | "email" | "currentRole" | "targetRole" | "status" | "requestedAt";
type SortDirection = "asc" | "desc";

function formatDate(iso: string | null) {
  if (!iso) return "—";
  return formatTimestampDate(iso);
}

function ChevronUpIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="m18 15-6-6-6 6" />
    </svg>
  );
}

function ChevronDownIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function ChevronsUpDownIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="m7 15 5 5 5-5M7 9l5-5 5 5" />
    </svg>
  );
}

function FilterIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="M3 5h18M6 12h12M10 19h4" />
    </svg>
  );
}

function EyeIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function CheckIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

function XIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

function SortableHeader({
  label,
  sortKey,
  activeSortKey,
  direction,
  onSort,
}: {
  label: string;
  sortKey: SortKey;
  activeSortKey: SortKey | null;
  direction: SortDirection;
  onSort: (key: SortKey) => void;
}) {
  const isActive = activeSortKey === sortKey;
  return (
    <button
      type="button"
      onClick={() => onSort(sortKey)}
      className={`inline-flex items-center gap-1 whitespace-nowrap hover:text-text ${isActive ? "text-text" : ""}`}
      aria-label={label}
    >
      {label}
      {isActive ? (
        direction === "asc" ? (
          <ChevronUpIcon className="h-3.5 w-3.5" />
        ) : (
          <ChevronDownIcon className="h-3.5 w-3.5" />
        )
      ) : (
        <ChevronsUpDownIcon className="h-3.5 w-3.5 text-text-subtle" />
      )}
    </button>
  );
}

function compareRequests(
  a: RoleUpgradeRequestListItem,
  b: RoleUpgradeRequestListItem,
  key: SortKey,
  direction: SortDirection,
) {
  const dir = direction === "asc" ? 1 : -1;
  switch (key) {
    case "requestedAt":
      return (new Date(a.requestedAt).getTime() - new Date(b.requestedAt).getTime()) * dir;
    case "status":
      return a.status.localeCompare(b.status) * dir;
    case "currentRole":
      return a.currentRole.localeCompare(b.currentRole) * dir;
    case "targetRole":
      return a.targetRole.localeCompare(b.targetRole) * dir;
    case "readableId":
      return (a.readableId ?? "").localeCompare(b.readableId ?? "") * dir;
    case "firstName":
      return (a.firstName ?? "").localeCompare(b.firstName ?? "") * dir;
    case "email":
      return (a.email ?? "").localeCompare(b.email ?? "") * dir;
  }
}

function DecisionDialog({
  request,
  decision,
  onSubmit,
  isPending,
  children,
}: {
  request: RoleUpgradeRequestListItem;
  decision: "approve" | "reject";
  onSubmit: (remarks: string) => Promise<void>;
  isPending: boolean;
  children: ReactNode;
}) {
  const intl = useIntl();
  const [remarks, setRemarks] = useState("");
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setError(undefined);
  };

  const handleSubmit = async () => {
    setError(undefined);
    try {
      await onSubmit(remarks);
      setOpen(false);
      setRemarks("");
    } catch (err) {
      setError(isQueryApiError(err) ? err.message : intl.formatMessage({ id: "lender.members.decisionError" }));
    }
  };

  const titleId =
    decision === "approve"
      ? "lender.members.approveDialogTitle"
      : "lender.members.rejectDialogTitle";
  const descId =
    decision === "approve"
      ? "lender.members.approveDialogDescription"
      : "lender.members.rejectDialogDescription";
  const placeholderId =
    decision === "approve"
      ? "lender.members.approveRemarksPlaceholder"
      : "lender.members.rejectRemarksPlaceholder";
  const verbId =
    decision === "approve" ? "lender.members.actionApprove" : "lender.members.actionReject";

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <FormattedMessage id={titleId} />
          </DialogTitle>
          <DialogDescription>
            <FormattedMessage
              id={descId}
              values={{ user: request.firstName, id: request.readableId }}
            />
          </DialogDescription>
        </DialogHeader>
        <div className="mt-4">
          <Textarea
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder={intl.formatMessage({ id: placeholderId })}
            rows={3}
          />
        </div>
        {error && (
          <AlertBanner status="error" className="mt-4">
            {error}
          </AlertBanner>
        )}
        <div className="mt-6 flex justify-end gap-3">
          <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
            <FormattedMessage id="lender.shared.cancelButton" />
          </Button>
          <Button
            type="button"
            variant={decision === "reject" ? "destructive" : "primary"}
            onClick={handleSubmit}
            disabled={isPending || !remarks.trim()}
          >
            <FormattedMessage id={verbId} />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ProfileDialog({ request }: { request: RoleUpgradeRequestListItem }) {
  const intl = useIntl();
  const { data: profile, isLoading, isError } = useGetAdminUserProfileQuery(request.userId);

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          aria-label={intl.formatMessage({ id: "lender.members.actionView" })}
          className="inline-flex items-center justify-center text-text-muted hover:text-accent-600"
        >
          <EyeIcon className="h-4 w-4" />
        </button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <FormattedMessage id="lender.members.profileDialogTitle" values={{ user: request.firstName }} />
          </DialogTitle>
          <DialogDescription>
            <FormattedMessage
              id="lender.members.profileDialogDescription"
              values={{ id: request.readableId, email: request.email, phone: request.phone }}
            />
          </DialogDescription>
        </DialogHeader>
        <div className="mt-4">
          {isLoading ? (
            <Text size="sm" variant="muted">
              <FormattedMessage id="lender.members.profileLoading" />
            </Text>
          ) : isError ? (
            <Text size="sm" variant="muted">
              <FormattedMessage id="lender.members.profileLoadError" />
            </Text>
          ) : profile ? (
            <div className="space-y-4">
              <dl className="divide-y divide-border">
                <div className="flex justify-between py-2">
                  <dt className="text-sm text-text-muted">
                    <FormattedMessage id="lender.members.profileFullNameLabel" />
                  </dt>
                  <dd className="text-sm font-medium text-text">
                    {profile.firstName} {profile.middleName} {profile.lastName}
                  </dd>
                </div>
                <div className="flex justify-between py-2">
                  <dt className="text-sm text-text-muted">
                    <FormattedMessage id="lender.profile.emailLabel" />
                  </dt>
                  <dd className="text-sm font-medium text-text">{profile.email}</dd>
                </div>
                <div className="flex justify-between py-2">
                  <dt className="text-sm text-text-muted">
                    <FormattedMessage id="lender.profile.phoneLabel" />
                  </dt>
                  <dd className="text-sm font-medium text-text">{profile.phone}</dd>
                </div>
                <div className="flex justify-between py-2">
                  <dt className="text-sm text-text-muted">
                    <FormattedMessage id="lender.profile.panLabel" />
                  </dt>
                  <dd className="text-sm font-medium text-text">{profile.pan ?? "—"}</dd>
                </div>
                <div className="flex justify-between py-2">
                  <dt className="text-sm text-text-muted">
                    <FormattedMessage id="lender.profile.aadhaarLabel" />
                  </dt>
                  <dd className="text-sm font-medium text-text">{profile.aadhaar ?? "—"}</dd>
                </div>
                <div className="flex justify-between py-2">
                  <dt className="text-sm text-text-muted">
                    <FormattedMessage id="lender.profile.dateOfBirthLabel" />
                  </dt>
                  <dd className="text-sm font-medium text-text">{profile.dob ? formatWireDate(profile.dob) : "—"}</dd>
                </div>
              </dl>
              {profile.addresses.length > 0 && (
                <div>
                  <dt className="text-sm text-text-muted mb-1">
                    <FormattedMessage id="lender.profile.addressesHeading" />
                  </dt>
                  {profile.addresses.map((addr) => (
                    <dd key={addr.id} className="text-sm font-medium text-text">
                      {addr.line1}, {addr.city}, {addr.state} {addr.pincode}
                    </dd>
                  ))}
                </div>
              )}
            </div>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function MembersList() {
  const intl = useIntl();
  const { data: requests, isLoading } = useListRoleUpgradeRequestsQuery(undefined);
  const [upgradeUserRole, { isLoading: isProcessing }] = useUpgradeUserRoleMutation();
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [statusFilter, setStatusFilter] = useState<RoleUpgradeStatusFilter | "">("");
  const [filtersOpen, setFiltersOpen] = useState(false);

  function handleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDirection("desc");
    }
  }

  const hasActiveFilters = statusFilter !== "";
  const activeFilterCount = statusFilter !== "" ? 1 : 0;

  function handleClearFilters() {
    setStatusFilter("");
    setPage(1);
  }

  const searched = requests
    ? requests.filter((req) => {
        if (!query) return true;
        const q = query.toLowerCase();
        return (
          req.firstName?.toLowerCase().includes(q) ||
          req.readableId?.toLowerCase().includes(q) ||
          req.email?.toLowerCase().includes(q) ||
          req.phone?.toLowerCase().includes(q) ||
          req.currentRole?.toLowerCase().includes(q) ||
          req.targetRole?.toLowerCase().includes(q)
        );
      })
    : null;

  const statusFiltered =
    searched && statusFilter ? searched.filter((req) => req.status === statusFilter) : searched;
  const filtered =
    statusFiltered && sortKey
      ? [...statusFiltered].sort((a, b) => compareRequests(a, b, sortKey, sortDirection))
      : statusFiltered;

  const totalCount = filtered?.length ?? 0;
  const pageCount = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const startIndex = (page - 1) * PAGE_SIZE;
  const pageItems = filtered?.slice(startIndex, startIndex + PAGE_SIZE) ?? [];
  const rangeStart = totalCount === 0 ? 0 : startIndex + 1;
  const rangeEnd = Math.min(startIndex + PAGE_SIZE, totalCount);

  async function handleApprove(request: RoleUpgradeRequestListItem, remarks: string) {
    await upgradeUserRole({
      userId: request.userId,
      body: {
        targetRole: request.targetRole,
        remarks,
        decision: "APPROVE",
      },
    }).unwrap();
  }

  async function handleReject(request: RoleUpgradeRequestListItem, remarks: string) {
    await upgradeUserRole({
      userId: request.userId,
      body: {
        targetRole: request.targetRole,
        remarks,
        decision: "REJECT",
      },
    }).unwrap();
  }

  const columns: DataTableColumn<RoleUpgradeRequestListItem>[] = [
    {
      id: "readableId",
      header: (
        <SortableHeader
          label={intl.formatMessage({ id: "lender.members.columnUserId" })}
          sortKey="readableId"
          activeSortKey={sortKey}
          direction={sortDirection}
          onSort={handleSort}
        />
      ),
      cell: (req) => <span className={`font-medium ${tabularNumsClass}`}>{req.readableId}</span>,
      cellClassName: `whitespace-nowrap ${tabularNumsClass}`,
    },
    {
      id: "firstName",
      header: (
        <SortableHeader
          label={intl.formatMessage({ id: "lender.members.columnName" })}
          sortKey="firstName"
          activeSortKey={sortKey}
          direction={sortDirection}
          onSort={handleSort}
        />
      ),
      cell: (req) => req.firstName,
      cellClassName: "whitespace-nowrap font-medium text-text",
    },
    {
      id: "email",
      header: (
        <SortableHeader
          label={intl.formatMessage({ id: "lender.members.columnEmail" })}
          sortKey="email"
          activeSortKey={sortKey}
          direction={sortDirection}
          onSort={handleSort}
        />
      ),
      cell: (req) => req.email,
      cellClassName: "whitespace-nowrap",
    },
    {
      id: "currentRole",
      header: (
        <SortableHeader
          label={intl.formatMessage({ id: "lender.members.columnCurrentRole" })}
          sortKey="currentRole"
          activeSortKey={sortKey}
          direction={sortDirection}
          onSort={handleSort}
        />
      ),
      cell: (req) => <span className="capitalize">{req.currentRole.toLowerCase()}</span>,
      cellClassName: "whitespace-nowrap",
    },
    {
      id: "targetRole",
      header: (
        <SortableHeader
          label={intl.formatMessage({ id: "lender.members.columnTargetRole" })}
          sortKey="targetRole"
          activeSortKey={sortKey}
          direction={sortDirection}
          onSort={handleSort}
        />
      ),
      cell: (req) => <span className="capitalize">{req.targetRole.toLowerCase()}</span>,
      cellClassName: "whitespace-nowrap",
    },
    {
      id: "status",
      header: (
        <SortableHeader
          label={intl.formatMessage({ id: "lender.members.columnStatus" })}
          sortKey="status"
          activeSortKey={sortKey}
          direction={sortDirection}
          onSort={handleSort}
        />
      ),
      cell: (req) => (
        <Badge status={STATUS_BADGE[req.status] ?? "info"}>
          <FormattedMessage id={`lender.members.status${req.status}`} />
        </Badge>
      ),
      cellClassName: "whitespace-nowrap",
    },
    {
      id: "requestedAt",
      header: (
        <SortableHeader
          label={intl.formatMessage({ id: "lender.members.columnRequestedAt" })}
          sortKey="requestedAt"
          activeSortKey={sortKey}
          direction={sortDirection}
          onSort={handleSort}
        />
      ),
      cell: (req) => formatDate(req.requestedAt),
      cellClassName: `whitespace-nowrap ${tabularNumsClass}`,
    },
    {
      id: "action",
      header: intl.formatMessage({ id: "lender.members.columnAction" }),
      cell: (req) => (
        <div className="flex items-center gap-1">
          <ProfileDialog request={req} />
          {req.status === "IN_PROGRESS" && (
            <>
              <DecisionDialog request={req} decision="approve" onSubmit={(remarks) => handleApprove(req, remarks)} isPending={isProcessing}>
                <button
                  type="button"
                  aria-label={intl.formatMessage({ id: "lender.members.actionApprove" })}
                  className="inline-flex items-center justify-center text-text-muted hover:text-accent-600"
                >
                  <CheckIcon className="h-4 w-4" />
                </button>
              </DecisionDialog>
              <DecisionDialog request={req} decision="reject" onSubmit={(remarks) => handleReject(req, remarks)} isPending={isProcessing}>
                <button
                  type="button"
                  aria-label={intl.formatMessage({ id: "lender.members.actionReject" })}
                  className="inline-flex items-center justify-center text-text-muted hover:text-error"
                >
                  <XIcon className="h-4 w-4" />
                </button>
              </DecisionDialog>
            </>
          )}
        </div>
      ),
      cellClassName: "whitespace-nowrap",
    },
  ];

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <Heading level={2}>
          <FormattedMessage id="lender.members.heading" />
        </Heading>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="max-w-sm flex-1">
          <SearchInput
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onClear={() => {
              setQuery("");
              setPage(1);
            }}
            placeholder={intl.formatMessage({ id: "lender.members.searchPlaceholder" })}
          />
        </div>

        <Popover open={filtersOpen} onOpenChange={setFiltersOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="relative inline-flex items-center gap-2 rounded-sm border border-border bg-surface-raised px-3 py-2 text-sm font-medium text-text hover:bg-surface"
            >
              <FilterIcon className="h-4 w-4" />
              <FormattedMessage id="lender.members.filtersButton" />
              {activeFilterCount > 0 && (
                <span className="flex h-4 w-4 items-center justify-center rounded-full bg-accent-600 text-[10px] font-semibold text-on-accent">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="flex flex-col gap-4">
            <SelectField
              label={intl.formatMessage({ id: "lender.members.columnStatus" })}
              placeholder={intl.formatMessage({ id: "lender.members.filterAllStatuses" })}
              value={statusFilter}
              onValueChange={(value) => {
                setStatusFilter(value as RoleUpgradeStatusFilter);
                setPage(1);
              }}
              options={ROLE_UPGRADE_STATUS_FILTERS.map((status) => ({
                value: status,
                label: intl.formatMessage({ id: `lender.members.status${status}` }),
              }))}
            />
            <div className="flex justify-end">
              <Button
                type="button"
                variant="ghost"
                density="compact"
                onClick={handleClearFilters}
                disabled={!hasActiveFilters}
              >
                <FormattedMessage id="lender.members.clearFiltersButton" />
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <DataTable
        columns={columns}
        rows={pageItems}
        rowKey={(req) => req.userId}
        loading={isLoading}
        titleColumnId="firstName"
        metaColumnId="status"
        emptyState={
          <EmptyState>
            <FormattedMessage
              id={hasActiveFilters ? "lender.members.noMatchingRequestsMessage" : "lender.members.emptyStateMessage"}
            />
          </EmptyState>
        }
      />

      <div className="mt-4 flex items-center justify-between">
        <Text size="sm" variant="muted">
          <FormattedMessage id="lender.creditor.rowsPerPageLabel" values={{ count: PAGE_SIZE }} />
        </Text>
        <div className="flex items-center gap-3">
          <Text size="sm" variant="muted">
            <FormattedMessage
              id="lender.creditor.paginationRangeLabel"
              values={{ start: rangeStart, end: rangeEnd, total: totalCount }}
            />
          </Text>
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
            aria-label={intl.formatMessage({ id: "lender.shared.previousPageLabel" })}
            className="text-text-muted disabled:opacity-40"
          >
            ‹
          </button>
          <button
            type="button"
            disabled={page >= pageCount}
            onClick={() => setPage((current) => Math.min(pageCount, current + 1))}
            aria-label={intl.formatMessage({ id: "lender.shared.nextPageLabel" })}
            className="text-text-muted disabled:opacity-40"
          >
            ›
          </button>
        </div>
      </div>
    </div>
  );
}
